import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { PlayFab } from './services/PlayFab';
import { Account } from './services/Account';
import './HandHistoryPage.css'; // Reuse styles for now

interface SessionListPageState {
  sessions: string[];
  loading: boolean;
  error: string | null;
}

const SessionListPage: React.FC = () => {
  const { playfabId } = useParams<{ playfabId: string }>();
  const [state, setState] = useState<SessionListPageState>({
    sessions: [],
    loading: true,
    error: null,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const loadSessions = async () => {
      if (!playfabId) {
        setState(prevState => ({ ...prevState, loading: false, error: 'User ID missing from URL' }));
        return;
      }
      setState(prevState => ({ ...prevState, loading: true, error: null }));
      console.log(`SessionListPage: Starting to load sessions for user ${playfabId}...`);

      try {
        const result = await PlayFab.executeCloudScript('getDynamoDBData', {
          action: 'getSessions',
          ownerPlayfabId: playfabId
        });

        console.log('SessionListPage: CloudScript getDynamoDBData (getSessions) response:', result);

        const nestedResult = result.data?.FunctionResult?.FunctionResult;

        if (result.success && nestedResult?.success) {
          let sessionIds: string[] = nestedResult.data;
          if (Array.isArray(sessionIds)) {
            // Filter out any potentially empty/null session IDs explicitly
            sessionIds = sessionIds.filter(id => id && typeof id === 'string' && id.trim() !== ''); 
            console.log('SessionListPage: Successfully parsed and filtered sessions:', sessionIds);
            setState({
              sessions: sessionIds,
              loading: false,
              error: null
            });
          } else {
            console.error('SessionListPage: CloudScript returned unexpected data format for sessions:', sessionIds);
            throw new Error('Invalid data format received for sessions.');
          }
        } else {
          const errorMessage = nestedResult?.error || result.data?.FunctionResult?.error || 'Unknown error fetching sessions';
          console.error('SessionListPage: Error fetching sessions:', errorMessage, nestedResult?.details);
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('SessionListPage: Error in loadSessions:', error);
        setState({
          sessions: [],
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load sessions'
        });
      }
    };

    loadSessions();
  }, [playfabId]);

  const handleSessionClick = (sessionId: string) => {
    navigate(`/history/user/${playfabId}/sessions/${sessionId}`);
  };

  const { sessions, loading, error } = state;

  return (
    <div className="hand-history">
      <div className="hand-history__list">
        <h2>{playfabId === PlayFab.getPlayerId() ? 'Your Sessions' : `Sessions for ${playfabId}`}</h2>
        {loading ? (
          <div className="hand-history__loading">Loading sessions...</div>
        ) : error ? (
          <div className="hand-history__error">{error}</div>
        ) : sessions.length > 0 ? (
          sessions.map((sessionId) => (
            <button
              key={sessionId}
              onClick={() => handleSessionClick(sessionId)}
              className="hand-history__session-item"
            >
              Session: {sessionId}
            </button>
          ))
        ) : (
          <div className="hand-history__empty">
            No sessions found. Analyze some hands to see them here!
          </div>
        )}
      </div>
      <div style={{ flex: 2, padding: '1.5rem' }}>
        Select a session to view its hands.
      </div>
    </div>
  );
};

export default SessionListPage; 